// apps/admin/admin-main/src/pages/UberDeliveries/DeliveryDetail.tsx
import React, { useState } from 'react';
import {
  Box, Typography, Stack, TextField, Button, Divider, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, Alert, Link,
  CircularProgress,
} from '@mui/material';
import {
  OpenInNew as ExternalLinkIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
  VerifiedUser as PodIcon,
} from '@mui/icons-material';
import config from '../../config';
import { httpClient } from '../../components/fetchApiData';
import { UberDelivery } from './DeliveryCard';
import {
  STATUS_FIELD_MATRIX, TERMINAL_STATUSES, CANCEL_REASONS, STATUS_LABELS,
} from './constants';

interface Props {
  delivery: UberDelivery;
  onRefresh: () => void;
}

export default function DeliveryDetail({ delivery, onRefresh }: Props) {
  const [edits, setEdits] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('out_of_items');
  const [cancelDescription, setCancelDescription] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [pod, setPod] = useState<any>(null);
  const [podLoading, setPodLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const allowedFields = STATUS_FIELD_MATRIX[delivery.status] || [];
  const isTerminal = TERMINAL_STATUSES.includes(delivery.status);
  const hasEdits = Object.keys(edits).length > 0;

  const isFieldEditable = (field: string) => allowedFields.includes(field);

  const setField = (field: string, value: any) => {
    setEdits((prev) => ({ ...prev, [field]: value }));
  };

  const apiCall = async (method: string, path: string, body?: any) => {
    const { json } = await httpClient(`${config.API_URL}/admin/uber-deliveries${path}`, {
      method,
      body: body ? JSON.stringify(body) : undefined,
    });
    return json;
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiCall('PATCH', `/${delivery.id}`, edits);
      setEdits({});
      setSuccess('Delivery updated');
      onRefresh();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: any) {
      setError(e.message);
    }
    setSaving(false);
  };

  const handleCancel = async () => {
    setCancelling(true);
    setError(null);
    try {
      await apiCall('POST', `/${delivery.id}/cancel`, {
        cancelation_reason: cancelReason,
        ...(cancelDescription && { additional_description: cancelDescription }),
      });
      setCancelOpen(false);
      setSuccess('Delivery cancelled');
      onRefresh();
    } catch (e: any) {
      setError(e.message);
    }
    setCancelling(false);
  };

  const handlePod = async () => {
    setPodLoading(true);
    setError(null);
    try {
      const result = await apiCall('GET', `/${delivery.id}/proof-of-delivery`);
      setPod(result);
    } catch (e: any) {
      setError(e.message);
    }
    setPodLoading(false);
  };

  return (
    <Box sx={{ p: 2, bgcolor: '#fafafa', borderTop: '1px solid', borderColor: 'divider' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Info Section */}
      <Stack spacing={1.5} mb={2}>
        <Typography variant="subtitle2" fontWeight={700}>Delivery Info</Typography>

        <Stack direction="row" spacing={4}>
          <Box flex={1}>
            <Typography variant="caption" color="text.secondary">Pickup</Typography>
            <Typography variant="body2">{delivery.pickup?.name || '\u2014'}</Typography>
            <Typography variant="caption" color="text.secondary">{delivery.pickup?.address || '\u2014'}</Typography>
          </Box>
          <Box flex={1}>
            <Typography variant="caption" color="text.secondary">Dropoff</Typography>
            <Typography variant="body2">{delivery.dropoff?.name || '\u2014'}</Typography>
            <Typography variant="caption" color="text.secondary">{delivery.dropoff?.address || '\u2014'}</Typography>
          </Box>
        </Stack>

        {delivery.courier && (
          <Stack direction="row" spacing={2}>
            <Typography variant="body2">
              Courier: <strong>{delivery.courier.name || '\u2014'}</strong>
            </Typography>
            {delivery.courier.phone_number && (
              <Typography variant="body2">{delivery.courier.phone_number}</Typography>
            )}
            {delivery.courier.vehicle_type && (
              <Chip label={delivery.courier.vehicle_type} size="small" variant="outlined" />
            )}
          </Stack>
        )}

        {delivery.tracking_url && (
          <Link href={delivery.tracking_url} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Track on Uber <ExternalLinkIcon sx={{ fontSize: 14 }} />
          </Link>
        )}

        {delivery.deeliver_order && (
          <Typography variant="body2">
            Order: <Link href={`/#/orders/${delivery.deeliver_order.id}`}>{delivery.deeliver_order.orderNumber}</Link>
            {' '}({delivery.deeliver_order.status})
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary">
          Fee: R{((delivery.fee || 0) / 100).toFixed(2)} | Tip: R{((delivery.tip || 0) / 100).toFixed(2)} | Status: {STATUS_LABELS[delivery.status] || delivery.status}
        </Typography>
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Editable Fields */}
      {!isTerminal && (
        <>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>Edit Delivery</Typography>
          <Stack spacing={1.5}>
            {isFieldEditable('dropoff_notes') && (
              <TextField
                label="Dropoff Notes"
                size="small"
                fullWidth
                defaultValue={delivery.dropoff_notes || ''}
                onChange={(e) => setField('dropoff_notes', e.target.value)}
              />
            )}
            {isFieldEditable('pickup_notes') && (
              <TextField
                label="Pickup Notes"
                size="small"
                fullWidth
                defaultValue={delivery.pickup_notes || ''}
                onChange={(e) => setField('pickup_notes', e.target.value)}
              />
            )}
            {isFieldEditable('tip_by_customer') && (
              <TextField
                label="Tip (cents)"
                size="small"
                type="number"
                fullWidth
                defaultValue={delivery.tip || 0}
                helperText="Can only be increased"
                onChange={(e) => setField('tip_by_customer', parseInt(e.target.value, 10))}
              />
            )}
            {hasEdits && (
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                size="small"
              >
                Save Changes
              </Button>
            )}
          </Stack>

          <Box mt={2}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => setCancelOpen(true)}
              size="small"
            >
              Cancel Delivery
            </Button>
          </Box>
        </>
      )}

      {/* Proof of Delivery */}
      {delivery.status === 'delivered' && (
        <Box mt={2}>
          <Button
            variant="outlined"
            startIcon={podLoading ? <CircularProgress size={16} /> : <PodIcon />}
            onClick={handlePod}
            disabled={podLoading}
            size="small"
          >
            View Proof of Delivery
          </Button>
          {pod?.verification && (
            <Box mt={1} p={1} bgcolor="#f5f5f5" borderRadius={1}>
              <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(pod.verification, null, 2)}
              </pre>
            </Box>
          )}
        </Box>
      )}

      {/* Cancel Dialog */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Delivery</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Reason</InputLabel>
            <Select value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} label="Reason">
              {CANCEL_REASONS.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Additional Description (optional)"
            fullWidth
            multiline
            rows={2}
            sx={{ mt: 2 }}
            value={cancelDescription}
            onChange={(e) => setCancelDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Back</Button>
          <Button
            onClick={handleCancel}
            color="error"
            variant="contained"
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={16} /> : undefined}
          >
            Confirm Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
